<template>
  <div
    :class="{ hidden: $route.path.split('/')[1] === 'districts' || $route.path.split('/')[1] === 'game' }"
    class="mobile-container"
  >
    <div class="mobile-layout-content">
      <ComposedHalloweenEventVisuals :user-data="userData" :address="address" @reset="store.openedBuilding = null" />

      <RealtyModal v-model="rent.isManorModalOpen" @closed="store.openedBuilding = null" />
      <RealtyAnounceModal v-model="rent.$state.announceModalOpen" />

      <transition name="fade-in">
        <AppHeader v-if="showUI" />
      </transition>

      <transition name="fade-in">
        <MobileHeaderButtons v-if="showUI && !!$route.path.split('/')[1]" />
      </transition>

      <NuxtPage class="nuxt-page-content" />

      <transition name="fade-in">
        <MobileFooter v-if="showUI && !!$route.path.split('/')[1]" />
      </transition>

      <ClientOnly>
        <DexModal v-if="isShowDexModal" v-model:visible="isShowDexModal" />
      </ClientOnly>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { Assets } from 'pixi.js';
import ComposedHalloweenEventVisuals from '~/components/Halloween/ComposedHalloweenEventVisuals.vue';
import type { IUserModel } from '~/types/apiService';
import { Buildings } from '~/utils/constants';
import { useBackgroundMusic } from '~/composables/useBackgroundMusic';
import RealtyAnounceModal from '~/widgets/realty/ui/AnounceModal.vue';
import RealtyModal from '~/widgets/realty/ui/RealtyModal.vue';
import { useRentEventStore } from '~/stores/rent-event-store';

const { data: userData } = useNuxtData<IUserModel>('userData');
const { address } = useWeb3ModalAccount();

const store = useMainStore();
const { data: sellerData, statusWS, isSellerOpen } = useStreetMerchant();
const { modalStatus } = useStreetMerchantStatus(isSellerOpen);
const { locale } = useI18n();
const route = useRoute();
const rent = useRentEventStore();

const showUI = ref(false);
const isShowDexModal = ref(false);

const showDexModal = () => {
  store.openedBuilding = Buildings.Dex;
  isShowDexModal.value = true;
};

const onLoaderAnimationEnd = () => {
  showUI.value = true;
};

const updateShowUI = (path: string) => {
  showUI.value = path.split('/')[1] !== 'game';
};

useHead({
  htmlAttrs: {
    lang: locale
  }
});

useBackgroundMusic();

provide('onLoaderAnimationEnd', onLoaderAnimationEnd);
provide('sellerData', sellerData);
provide('sellerStatusWS', statusWS);
provide('sellerIsOpen', isSellerOpen);
provide('sellerModalStatus', modalStatus);
provide('showDexModal', showDexModal);

onMounted(() => {
  const currentMonth = new Date().getMonth();
  const isWinterMonth = currentMonth === 11 || currentMonth === 0;
  store.setIsNewYearTheme(isWinterMonth);

  updateShowUI(route.path);

  Assets.add({ alias: 'doorVideo', src: '/door.mp4' });

  Assets.load(['doorVideo']);

  window.addEventListener('beforeunload', () => {
    resetHashHistory();
  });

  // Prevent zoom for Mobile
  document.addEventListener('gesturestart', function (e) {
    e.preventDefault();
  });

  // Prevent refreshing page on swipe down
  useHead({
    bodyAttrs: {
      class: 'prevent-refresh-page-mobile metamask-body-min-height'
    }
  });
});

watch(
  () => route.path,
  (path) => {
    updateShowUI(path);
  }
);
</script>

<style lang="scss" scoped>
.bottom-toolbar {
  bottom: 0;
  left: 0;
  position: absolute;
}
.mobile-container {
  height: 100dvh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
}

.mobile-layout-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nuxt-page-content {
  flex-grow: 1;
}

.hidden {
  overflow: hidden;
}

.fade-in-enter-active {
  transition: opacity 2s linear;
}

.fade-in-enter-from {
  opacity: 0;
}

.fade-in-enter-to {
  opacity: 1;
}
</style>

<style>
.mobile-container {
  header {
    max-height: 80px;
  }

  .header-logo.new-year-logo {
    height: 50px;
  }

  .main-header-container {
    height: 80px;
  }
  .header-wrapper {
    padding-top: 15px;
    padding-bottom: 0px;
    border-bottom: none;
  }
}
</style>
